import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { WarningIcon } from '@phosphor-icons/react';
import { CreateColumn, ProjectColumn } from 'shared/types';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useColumnMutations } from '@/hooks/useColumnMutations';
import { defineModal } from '@/lib/modals';

import { COLUMN_COLOR_OPTIONS } from '@/constants/columnColors';

export interface CreateColumnDialogProps {
  projectId: string;
}

export type CreateColumnDialogResult =
  | { status: 'saved'; column: ProjectColumn }
  | { status: 'canceled' };

const CreateColumnDialogImpl = NiceModal.create<CreateColumnDialogProps>(
  ({ projectId }) => {
    const { t } = useTranslation(['settings', 'common']);
    const modal = useModal();
    const [name, setName] = useState('');
    const [color, setColor] = useState('--neutral-foreground');

    const { createColumn } = useColumnMutations({
      onCreateSuccess: (column) => {
        modal.resolve({
          status: 'saved',
          column,
        } as CreateColumnDialogResult);
        modal.hide();
      },
      onCreateError: () => {},
    });

    const handleCreate = () => {
      if (!name.trim()) return;

      const createData: CreateColumn = {
        name: name.trim(),
        color,
      };

      createColumn.mutate({ projectId, data: createData });
    };

    const handleCancel = () => {
      modal.resolve({ status: 'canceled' } as CreateColumnDialogResult);
      modal.hide();
    };

    const handleOpenChange = (open: boolean) => {
      if (!open) {
        handleCancel();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && name.trim() && !createColumn.isPending) {
        e.preventDefault();
        handleCreate();
      }
    };

    return (
      <Dialog open={modal.visible} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('settings.columns.create.title')}</DialogTitle>
            <DialogDescription>
              {t('settings.columns.create.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="column-name">
                {t('settings.columns.create.nameLabel')}
              </Label>
              <Input
                id="column-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('settings.columns.create.namePlaceholder')}
                autoFocus
                disabled={createColumn.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="column-color">
                {t('settings.columns.create.colorLabel')}
              </Label>
              <div className="grid grid-cols-7 gap-2">
                {COLUMN_COLOR_OPTIONS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setColor(c.value)}
                    disabled={createColumn.isPending}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      color === c.value
                        ? 'border-foreground scale-110'
                        : 'border-transparent'
                    }`}
                    style={{ backgroundColor: c.color }}
                    title={c.label}
                  />
                ))}
              </div>
            </div>
          </div>

          {createColumn.isError && (
            <Alert variant="destructive">
              <WarningIcon className="h-4 w-4" />
              <AlertDescription>
                {createColumn.error instanceof Error
                  ? createColumn.error.message
                  : t('settings.columns.create.errors.createFailed')}
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={createColumn.isPending}
            >
              {t('common:buttons.cancel')}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!name.trim() || createColumn.isPending}
            >
              {createColumn.isPending
                ? t('common:states.saving')
                : t('common:buttons.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
);

export const CreateColumnDialog = defineModal<
  CreateColumnDialogProps,
  CreateColumnDialogResult
>(CreateColumnDialogImpl);
